import { useEffect } from 'react';
import BookImage from './BookImage';

const BookModal = ({ book, isOpen, onClose }) => {
  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !book) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Header with book image and basic info */}
          <div className="flex gap-6 mb-6">
            {/* Book image */}
            <div className="flex-shrink-0">
              <div className="relative w-32 h-44 transform transition-transform duration-300 hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 to-blue-100/20 rounded-xl" />
                <BookImage
                  src={book.thumbnail}
                  alt={book.title}
                  className="w-full h-full object-cover rounded-xl shadow-lg relative z-10"
                />
                <div className="absolute -inset-1 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-xl blur-md z-0" />
              </div>
            </div>

            {/* Book details */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 font-fraunces leading-tight mb-2">
                {book.title}
              </h2>
              <p className="text-lg text-gray-600 font-space mb-3">
                by {book.author}
              </p>
              
              {/* Rating and year */}
              <div className="flex items-center gap-4 mb-4">
                {book.rating && (
                  <div className="flex items-center gap-1">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(book.rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="text-sm text-gray-600 font-medium">
                      {book.rating}
                    </span>
                  </div>
                )}
                
                {book.year && (
                  <span className="text-sm text-gray-500 font-space">
                    Published {book.year}
                  </span>
                )}
              </div>

              {/* Genres */}
              {book.genres && book.genres.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {book.genres.map(genre => (
                    <span
                      key={genre}
                      className="px-3 py-1 bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 text-sm font-medium rounded-full border border-purple-100 font-space"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              )}

              {/* Category */}
              {book.category && (
                <div className="mb-4">
                  <span className="text-sm text-gray-500 font-space">Category:</span>
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    {book.category}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 font-fraunces">
              About this book
            </h3>
            <p className="text-gray-700 leading-relaxed font-space">
              {book.explanation}
            </p>
          </div>

          {/* Buy button */}
          {book.amazonLink && (
            <div className="flex justify-center">
              <a
                href={book.amazonLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-space"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Buy Now
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookModal; 